// src/pages/AddMetrics.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subMonths } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { 
  QuestionMarkCircleIcon, 
  CheckCircleIcon, 
  UserCircleIcon,
  PlusCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { addArtistMetrics } from '../services/artistService';
import { isAuthenticated } from '../services/authService';
import { getUserLinkedArtists, SpotifyArtist } from '../services/spotifyArtistService';
import { saveSelectedArtist, getSelectedArtist } from '../services/artistStorageService';
import LoadingState from '../components/common/LoadingState';
import ErrorMessage from '../components/common/ErrorMessage';
import ArtistSearch from '../components/artists/ArtistSearch';

const AddMetrics: React.FC = () => {
  const navigate = useNavigate();
  
  // State for month/year selection
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const formattedDate = format(selectedDate, 'MMMM yyyy', { locale: enUS });
  const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  const [formData, setFormData] = useState({
    streams: '',
    listeners: '',
    saves: '',
    follower: '',
    playlist_adds: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [newScore, setNewScore] = useState<number | null>(null);
  const [linkedArtists, setLinkedArtists] = useState<Array<{id: string, spotifyId: string, name: string}>>([]);
  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);
  const [showArtistSearch, setShowArtistSearch] = useState<boolean>(false);
  const [authChecked, setAuthChecked] = useState<boolean>(false);

  // Navigate to previous month
  const handlePreviousMonth = () => {
    setSelectedDate(prevDate => subMonths(prevDate, 1));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    // Don't allow selecting future months
    const nextMonth = new Date(selectedDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    if (nextMonth <= new Date()) {
      setSelectedDate(nextMonth);
    }
  };

  // Check auth status
  useEffect(() => {
    const checkAuth = async () => {
      const authenticated = isAuthenticated();
      console.log("AddMetrics: Auth status checked, authenticated:", authenticated);
      
      if (!authenticated) {
        // Redirect zur Login-Seite
        navigate('/login', { state: { returnPath: '/add-metrics' } });
        return;
      }
      
      setAuthChecked(true);
    };
    
    checkAuth();
  }, [navigate]);

  // Load linked artists when auth is confirmed
  useEffect(() => {
    const fetchLinkedArtists = async () => {
      if (!authChecked) return;
      
      try {
        setLoading(true);
        
        const artists = await getUserLinkedArtists();
        console.log("Linked artists loaded:", artists);
        setLinkedArtists(artists);
        
        // Get previously selected artist from storage
        const savedArtist = getSelectedArtist();
        
        if (savedArtist && savedArtist.id) {
          // Check if the saved artist exists in the fetched list
          const artistExists = artists.some(artist => 
            artist.id === savedArtist.id || artist.spotifyId === savedArtist.id
          );
          
          if (artistExists) {
            setSelectedArtistId(savedArtist.id);
          } else if (artists.length > 0) {
            // If the saved artist doesn't exist anymore, select the first one
            setSelectedArtistId(artists[0].id);
            saveSelectedArtist({
              id: artists[0].id,
              spotifyId: artists[0].spotifyId,
              name: artists[0].name
            });
          }
        } else if (artists.length > 0) {
          // If no saved artist, select the first one
          setSelectedArtistId(artists[0].id);
          saveSelectedArtist({
            id: artists[0].id,
            spotifyId: artists[0].spotifyId,
            name: artists[0].name
          });
        }
      } catch (err) {
        console.error('Error fetching linked artists:', err);
        setError('Error loading linked artists');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLinkedArtists();
  }, [authChecked]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Reset status when input changes
    setError(null);
    setSuccess(false);
  };

  const handleSelectArtist = (artistId: string) => {
    setSelectedArtistId(artistId);
    
    // Find the selected artist in the list
    const artist = linkedArtists.find(a => a.id === artistId || a.spotifyId === artistId);
    
    if (artist) {
      // Save selected artist to storage
      saveSelectedArtist({
        id: artist.id,
        spotifyId: artist.spotifyId,
        name: artist.name
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedArtistId) {
      setError('Please select an artist or link a new one');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Convert string values to numbers
      const metricsData = {
        streams: parseInt(formData.streams, 10),
        listeners: parseInt(formData.listeners, 10),
        saves: parseInt(formData.saves, 10),
        follower: parseInt(formData.follower, 10),
        playlist_adds: parseInt(formData.playlist_adds, 10),
        notes: formData.notes,
        // Add the selected month in YYYY-MM-01 format
        metric_date: format(selectedDate, 'yyyy-MM-01')
      };
      
      // Send data to API - use either the numeric ID or Spotify ID
      const response = await addArtistMetrics(selectedArtistId, metricsData);
      
      // Handle successful response
      if (response && response.success) {
        setSuccess(true);
        if (response.score) {
          setNewScore(response.score);
        }
        
        // Reset form
        setFormData({
          streams: '',
          listeners: '',
          saves: '',
          follower: '',
          playlist_adds: '',
          notes: ''
        });
        
        // Show success message for 5 seconds
        setTimeout(() => {
          setSuccess(false);
          setNewScore(null);
        }, 5000);
      } else {
        setError('Failed to submit metrics. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting metrics:', err);
      setError('Error submitting metrics. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArtistSelected = (artist: SpotifyArtist) => {
    // Close search and update linked artists list
    setShowArtistSearch(false);
    
    // Use Spotify ID as the selected ID initially
    setSelectedArtistId(artist.id);
    
    // Save selected artist to storage
    saveSelectedArtist({
      id: artist.id,
      spotifyId: artist.id,
      name: artist.name
    });
    
    // Add artist to list if not already present
    if (!linkedArtists.find(a => a.spotifyId === artist.id)) {
      setLinkedArtists(prev => [...prev, { 
        id: artist.id, // Initially use Spotify ID as ID, will be updated after refresh
        spotifyId: artist.id,
        name: artist.name
      }]);
    }
  };

  // Show loading indicator while loading or submitting
  if (loading) {
    return <LoadingState message="Loading artist profiles..." />;
  }

  if (submitting) {
    return <LoadingState message="Submitting data..." />;
  }

  const selectedArtist = linkedArtists.find(
    artist => artist.id === selectedArtistId || artist.spotifyId === selectedArtistId
  );

  return (
    <div className="container mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Monthly Metrics</h1>
        <p className="text-gray-600">
          {selectedArtist 
            ? `Enter metrics for ${selectedArtist.name}.`
            : `Select an artist and enter metrics.`
          }
        </p>
      </div>

      {error && (
        <ErrorMessage 
          title="Error" 
          message={error}
          onRetry={() => setError(null)}
        />
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5 mr-2" />
            <div>
              <h3 className="text-green-800 font-medium">Metrics submitted successfully!</h3>
              {newScore && (
                <p className="mt-1">The new artist score is <span className="font-bold">{newScore}</span>/100.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Artist selection */}
      {!showArtistSearch ? (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Select Artist</h2>
            <button
              onClick={() => setShowArtistSearch(true)}
              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              <PlusCircleIcon className="h-4 w-4 mr-1" />
              Add New Artist
            </button>
          </div>
          
          {linkedArtists.length > 0 ? (
            <div className="space-y-2">
              {linkedArtists.map(artist => (
                <div 
                  key={artist.id || artist.spotifyId}
                  className={`p-3 border rounded-md flex items-center cursor-pointer 
                    ${(selectedArtistId === artist.id || selectedArtistId === artist.spotifyId) 
                    ? 'bg-green-50 border-green-200' 
                    : 'hover:bg-gray-50'}`}
                  onClick={() => handleSelectArtist(artist.id || artist.spotifyId)}
                >
                  <UserCircleIcon className="h-6 w-6 text-gray-400 mr-2" />
                  <span className="font-medium">{artist.name}</span>
                  {(selectedArtistId === artist.id || selectedArtistId === artist.spotifyId) && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 ml-auto" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <p className="mb-2">No artists linked yet</p>
              <button
                onClick={() => setShowArtistSearch(true)}
                className="text-green-600 hover:text-green-800 text-sm font-medium"
              >
                Add your first artist
              </button>
            </div>
          )}
        </div>
      ) : (
        <ArtistSearch 
          onArtistSelected={handleArtistSelected} 
          showLinkButton={false}
        />
      )}

      {/* Month/Year Selector and Metrics form */}
      {selectedArtistId && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Enter metrics for {selectedArtist?.name || 'selected artist'}
          </h2>
          
          {/* Month/Year Selector */}
          <div className="mb-6 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-3">
            <button 
              onClick={handlePreviousMonth}
              className="p-1 rounded-full hover:bg-gray-200"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            
            <div className="flex items-center">
              <CalendarIcon className="h-5 w-5 text-gray-500 mr-2" />
              <span className="text-gray-700 font-medium">{capitalizedDate}</span>
              <div className="ml-2 text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                Metrics for last 28 days
              </div>
            </div>
            
            <button 
              onClick={handleNextMonth}
              className="p-1 rounded-full hover:bg-gray-200"
              disabled={selectedDate.getMonth() === new Date().getMonth() && 
                        selectedDate.getFullYear() === new Date().getFullYear()}
            >
              <ChevronRightIcon className={`h-5 w-5 ${selectedDate.getMonth() === new Date().getMonth() && 
                                           selectedDate.getFullYear() === new Date().getFullYear() ? 
                                           'text-gray-300' : 'text-gray-600'}`} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Streams */}
              <div>
                <label htmlFor="streams" className="block text-sm font-medium text-gray-700 mb-1">
                  Streams
                  <Tooltip text="Total number of streams across all tracks in the last month." />
                </label>
                <input
                  type="number"
                  id="streams"
                  name="streams"
                  value={formData.streams}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="e.g. 10000"
                  required
                />
              </div>

              {/* Monthly Listeners */}
              <div>
                <label htmlFor="listeners" className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Listeners
                  <Tooltip text="Number of unique listeners in the last month." />
                </label>
                <input
                  type="number"
                  id="listeners"
                  name="listeners"
                  value={formData.listeners}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="e.g. 2500"
                  required
                />
              </div>

              {/* Saves */}
              <div>
                <label htmlFor="saves" className="block text-sm font-medium text-gray-700 mb-1">
                  Saves
                  <Tooltip text="How many times your songs were saved to libraries." />
                </label>
                <input
                  type="number"
                  id="saves"
                  name="saves"
                  value={formData.saves}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="e.g. 250"
                  required
                />
              </div>

              {/* Follower */}
              <div>
                <label htmlFor="follower" className="block text-sm font-medium text-gray-700 mb-1">
                  Followers (Total)
                  <Tooltip text="Total number of followers (not just new ones)." />
                </label>
                <input
                  type="number"
                  id="follower"
                  name="follower"
                  value={formData.follower}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="e.g. 500"
                  required
                />
              </div>

              {/* Playlist Adds */}
              <div>
                <label htmlFor="playlist_adds" className="block text-sm font-medium text-gray-700 mb-1">
                  Playlist Additions
                  <Tooltip text="How many times your songs were added to playlists." />
                </label>
                <input
                  type="number"
                  id="playlist_adds"
                  name="playlist_adds"
                  value={formData.playlist_adds}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="e.g. 30"
                  required
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mt-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="E.g., New release, playlist feature, etc."
              />
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={submitting}
              >
                Save Metrics
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// Tooltip Component
interface TooltipProps {
  text: string;
}

const Tooltip: React.FC<TooltipProps> = ({ text }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <span className="relative ml-1 inline-block">
      <QuestionMarkCircleIcon
        className="h-4 w-4 text-gray-400 inline cursor-pointer"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      />
      {isVisible && (
        <div className="absolute z-10 w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg -left-32 bottom-6">
          {text}
          <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 -bottom-1 left-32"></div>
        </div>
      )}
    </span>
  );
};

export default AddMetrics;